import type { TypeBackground } from '@mui/material'
import type { PropsWithChildren } from 'react'
import React, { useContext, useEffect, useId } from 'react'

import type { StackHandler } from './useStackHandler'
import { useStackHandler } from './useStackHandler'

interface AnchorRef<T extends HTMLElement> {
  anchor: React.RefObject<T | null>
  setAnchor: (element: T | null) => void
}

interface AppLayout {
  header: AnchorRef<HTMLDivElement>
  action: AnchorRef<HTMLDivElement>
  background: StackHandler<keyof TypeBackground>
  headerHidden: StackHandler<boolean>
}
export const AppLayoutContext = React.createContext<AppLayout | null>(null)
AppLayoutContext.displayName = 'AppLayoutContext'

export const useAppLayoutHeaderAnchor = () => {
  const ctx = React.useContext(AppLayoutContext)
  if (!ctx) {
    throw new Error('useAppLayoutHeaderAnchor must be used within a AppLayoutContext')
  }

  return ctx.header
}

export const useAppLayoutActionAnchor = () => {
  const ctx = React.useContext(AppLayoutContext)
  if (!ctx) {
    throw new Error('useAppLayoutActionAnchor must be used within a AppLayoutContext')
  }

  return ctx.action
}

export const useAppLayoutBackground = (color: keyof TypeBackground | undefined) => {
  const ctx = useContext(AppLayoutContext)
  if (!ctx) {
    throw new Error('useAppLayoutBackground must be used within a AppLayoutContext')
  }

  const id = useId()

  useEffect(() => {
    if (color) {
      return ctx.background.set(color)
    }

    return undefined
  }, [color, ctx, id])
}

/**
 * Hide the unified app header bar (and its hamburger) while mounted with
 * `hidden`. For full-screen views that supply their own chrome — e.g. the
 * messaging workspace on a mobile thread, where the ThreadHeader is the bar.
 */
export const useHideAppHeader = (hidden: boolean) => {
  const ctx = useContext(AppLayoutContext)
  if (!ctx) {
    throw new Error('useHideAppHeader must be used within a AppLayoutContext')
  }

  useEffect(() => {
    if (hidden) {
      return ctx.headerHidden.set(true)
    }

    return undefined
  }, [hidden, ctx])
}

/**
 * The anchor has to be *state*, not a bare ref: consumers portal into it and
 * must re-render once it exists. With a ref, any page whose subtree never
 * re-renders after AppLayout commits (a fully synchronous view such as
 * Configuration) rendered `null` for its whole lifetime and silently lost its
 * title bar. `setAnchor` is the raw `useState` setter so its identity stays
 * stable — a changing ref callback would detach/re-attach on every render.
 */
function useAnchorRef<T extends HTMLElement>(): AnchorRef<T> {
  const [element, setAnchor] = React.useState<T | null>(null)

  return React.useMemo(
    () => ({
      anchor: { current: element },
      setAnchor,
    }),
    [element],
  )
}

export const AppLayoutProvider: React.FC<PropsWithChildren<NonNullable<unknown>>> = ({
  children,
}) => {
  const header = useAnchorRef<HTMLDivElement>()
  const action = useAnchorRef<HTMLDivElement>()
  const background = useStackHandler<keyof TypeBackground>()
  const headerHidden = useStackHandler<boolean>()

  return (
    <AppLayoutContext.Provider
      value={{
        action,
        background,
        header,
        headerHidden,
      }}
    >
      {children}
    </AppLayoutContext.Provider>
  )
}
